import type { AuthProvider } from "ra-core";
import { supabaseAuthProvider } from "ra-supabase-core";

import { canAccess } from "../commons/canAccess";
import { getSupabaseClient } from "./supabase";

const getBaseAuthProvider = () =>
  supabaseAuthProvider(getSupabaseClient(), {
    getIdentity: async () => {
      const sale = await getSale();

      if (sale == null) {
        throw new Error();
      }

      const callName =
        textValue(sale.display_name) || textValue(sale.first_name);

      return {
        id: sale.id,
        fullName: [textValue(sale.first_name), textValue(sale.last_name)]
          .filter((part) => part.length > 0)
          .join(" "),
        displayName: callName,
        avatar: avatarSrc(sale.avatar),
      };
    },
  });

// To speed up checks, we cache the initialization state
// and the current sale in the local storage. They are cleared on logout.
const IS_INITIALIZED_CACHE_KEY = "RaStore.auth.is_initialized";
const CURRENT_SALE_CACHE_KEY = "RaStore.auth.current_sale";

function getLocalStorage(): Storage | null {
  if (typeof window !== "undefined" && window.localStorage) {
    return window.localStorage;
  }
  return null;
}

export async function getIsInitialized() {
  const storage = getLocalStorage();
  const cachedValue = storage?.getItem(IS_INITIALIZED_CACHE_KEY);
  if (cachedValue != null) {
    return cachedValue === "true";
  }

  const { data } = await getSupabaseClient()
    .from("init_state")
    .select("is_initialized");
  const isInitialized = data?.at(0)?.is_initialized > 0;

  if (isInitialized) {
    storage?.setItem(IS_INITIALIZED_CACHE_KEY, "true");
  }

  return isInitialized;
}

interface CurrentSale {
  id: number;
  first_name?: string | null;
  last_name?: string | null;
  display_name?: string | null;
  avatar?: unknown;
  administrator?: boolean;
}

function isCurrentSale(value: unknown): value is CurrentSale {
  return (
    typeof value === "object" &&
    value !== null &&
    "id" in value &&
    "display_name" in value
  );
}

function textValue(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function avatarSrc(avatar: unknown): string | undefined {
  if (typeof avatar !== "object" || avatar === null || !("src" in avatar)) {
    return undefined;
  }
  return typeof avatar.src === "string" ? avatar.src : undefined;
}

const getSale = async () => {
  const storage = getLocalStorage();
  const cachedValue = storage?.getItem(CURRENT_SALE_CACHE_KEY);
  if (cachedValue != null) {
    try {
      const cachedSale: unknown = JSON.parse(cachedValue);
      if (isCurrentSale(cachedSale)) {
        return cachedSale;
      }
    } catch (error) {
      console.error("getSale: caché inválida", error);
      storage?.removeItem(CURRENT_SALE_CACHE_KEY);
    }
  }

  const { data: dataSession, error: errorSession } =
    await getSupabaseClient().auth.getSession();

  // Shouldn't happen after login but just in case
  if (dataSession?.session?.user == null || errorSession) {
    return undefined;
  }

  const { data: dataSale, error: errorSale } = await getSupabaseClient()
    .from("sales")
    .select("id, first_name, last_name, display_name, avatar, administrator")
    .match({ user_id: dataSession?.session?.user.id })
    .single();

  // Shouldn't happen either as all users are sales but just in case
  if (dataSale == null || errorSale) {
    return undefined;
  }

  try {
    const src = avatarSrc(dataSale.avatar);
    const cacheSale =
      src != null && src.length > 100_000
        ? { ...dataSale, avatar: null }
        : dataSale;
    storage?.setItem(CURRENT_SALE_CACHE_KEY, JSON.stringify(cacheSale));
  } catch (error) {
    console.error("getSale: no se pudo guardar la caché", error);
  }
  return dataSale;
};

export function clearCurrentSaleCache(): void {
  getLocalStorage()?.removeItem(CURRENT_SALE_CACHE_KEY);
}

function clearCache() {
  const storage = getLocalStorage();
  storage?.removeItem(IS_INITIALIZED_CACHE_KEY);
  storage?.removeItem(CURRENT_SALE_CACHE_KEY);
}

export const getAuthProvider = (): AuthProvider => {
  const baseAuthProvider = getBaseAuthProvider();
  return {
    ...baseAuthProvider,
    login: async (params) => {
      if (params.ssoDomain) {
        const { error } = await getSupabaseClient().auth.signInWithSSO({
          domain: params.ssoDomain,
        });
        if (error) {
          throw error;
        }
        return;
      }
      return baseAuthProvider.login(params);
    },
    logout: async (params) => {
      clearCache();
      return baseAuthProvider.logout(params);
    },
    checkAuth: async (params) => {
      // Users are on the set-password page, nothing to do
      if (
        window.location.pathname === "/set-password" ||
        window.location.hash.includes("#/set-password")
      ) {
        return;
      }
      // Users are on the forgot-password page, nothing to do
      if (
        window.location.pathname === "/forgot-password" ||
        window.location.hash.includes("#/forgot-password")
      ) {
        return;
      }
      // Users are on the sign-up page, nothing to do
      if (
        window.location.pathname === "/sign-up" ||
        window.location.hash.includes("#/sign-up")
      ) {
        return;
      }

      const isInitialized = await getIsInitialized();

      if (!isInitialized) {
        await getSupabaseClient().auth.signOut();
        throw {
          redirectTo: "/sign-up",
          message: false,
        };
      }

      return baseAuthProvider.checkAuth(params);
    },
    canAccess: async (params) => {
      try {
        const isInitialized = await getIsInitialized();
        if (!isInitialized) return false;

        const sale = await getSale();
        if (sale == null) return false;

        const role = sale.administrator ? "admin" : "user";
        return canAccess(role, params);
      } catch (error) {
        console.error("canAccess", error);
        clearCurrentSaleCache();
        return false;
      }
    },
    getAuthorizationDetails(authorizationId: string) {
      return getSupabaseClient().auth.oauth.getAuthorizationDetails(
        authorizationId,
      );
    },
    approveAuthorization(authorizationId: string) {
      return getSupabaseClient().auth.oauth.approveAuthorization(
        authorizationId,
      );
    },
    denyAuthorization(authorizationId: string) {
      return getSupabaseClient().auth.oauth.denyAuthorization(authorizationId);
    },
  };
};
